/**
 * SimplePatternStep Component - For SIMPLE pattern configuration
 * Used when user selected "Always display data (it's always available)"
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, CheckCircle2, ArrowRight, Code, Eye, Copy, Zap, TestTube } from 'lucide-react'
import { WizardState } from '../../../../types/macros'
import DataKeyInput from './shared/DataKeyInput'


export interface SimplePatternStepProps {
  wizardState: WizardState
  onUpdateState: (updates: Partial<WizardState>) => void
  onBack: () => void
}

const SimplePatternStep: React.FC<SimplePatternStepProps> = ({
  wizardState,
  onUpdateState,
  onBack
}) => {
  const [localState, setLocalState] = useState({
    dataKey: wizardState.patternData.simple?.dataKey || '',
    description: wizardState.patternData.simple?.description || ''
  })
  const [testValue, setTestValue] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [expandedExample, setExpandedExample] = useState<string | null>(null)

  // Update parent state when local state changes
  React.useEffect(() => {
    onUpdateState({
      patternData: {
        ...wizardState.patternData,
        simple: {
          dataKey: localState.dataKey,
          description: localState.description
        }
      }
    })
  }, [localState, onUpdateState, wizardState.patternData])

  // Validation
  const isValid = React.useMemo(() => {
    return localState.dataKey.trim() !== '' && 
           /^[a-zA-Z][a-zA-Z0-9_]*$/.test(localState.dataKey)
  }, [localState.dataKey])

  // Generate syntax
  const generatedSyntax = React.useMemo(() => {
    return localState.dataKey ? `{{${localState.dataKey}}}` : ''
  }, [localState.dataKey])

  // Interactive templates
  const templates = [
    {
      id: 'greeting',
      title: 'Personal Greeting',
      description: 'Personalized message with customer name',
      dataKey: 'first_name',
      example: 'Hi {{first_name}}, welcome back!',
      testValue: 'Sarah',
      result: 'Hi Sarah, welcome back!'
    },
    {
      id: 'order_confirm',
      title: 'Order Confirmation',
      description: 'Show order number in confirmation message',
      dataKey: 'order_id',
      example: 'Your order {{order_id}} has been confirmed.',
      testValue: '#ORD-12345',
      result: 'Your order #ORD-12345 has been confirmed.'
    },
    {
      id: 'product_name',
      title: 'Product Display',
      description: 'Show specific product in messages',
      dataKey: 'product_name',
      example: '{{product_name}} has been added to your cart.',
      testValue: 'iPhone 15 Pro',
      result: 'iPhone 15 Pro has been added to your cart.'
    },
    {
      id: 'username',
      title: 'Account Reference',
      description: 'Reference user account in system messages',
      dataKey: 'username',
      example: 'Account settings updated for {{username}}.',
      testValue: 'john_doe',
      result: 'Account settings updated for john_doe.'
    }
  ]

  const handleTemplateSelect = (template: typeof templates[0]) => {
    setLocalState({
      dataKey: template.dataKey,
      description: template.description
    })
    setTestValue(template.testValue)
    setExpandedExample(null)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          Simple Pattern Setup
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-md mx-auto">
          Learn how Simple macros work first, then configure your data key and test the final output.
        </p>
      </div>

      {/* Pattern Confirmation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-800 dark:text-green-300 mb-1">
              You Selected: Simple Pattern
            </h3>
            <p className="text-sm text-green-700 dark:text-green-400">
              This pattern works best when your data (like customer names) is always present. 
              The macro will directly replace with the actual value.
            </p>
          </div>
        </div>
      </motion.div>

      {/* How It Works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
      >
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
          How Simple Patterns Work
        </h3>
        <div className="space-y-2 text-sm text-blue-700 dark:text-blue-400">
          <p>• A Simple macro inserts one data value directly into your message</p>
          <p>• Syntax format: <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">{"{{data_key}}"}</code></p>
          <p>• Best when data is guaranteed to exist (required profile or order fields)</p>
          <p>• If data is missing, output becomes empty text at that spot</p>
        </div>
      </motion.div>

      {/* Smart Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800"
      >
        <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center">
          <Sparkles className="w-4 h-4 mr-2" />
          Smart Tips for Simple Patterns
        </h4>

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="font-medium text-blue-800 dark:text-blue-300">✅ Perfect for:</div>
            <ul className="space-y-1 text-blue-700 dark:text-blue-400">
              <li>• Customer names (first_name, last_name)</li>
              <li>• Order IDs (order_id, confirmation_number)</li>
              <li>• Product names (product_title, item_name)</li>
              <li>• User accounts (username, email)</li>
              <li>• Amounts (price, total, discount)</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-blue-800 dark:text-blue-300">⚠️ Consider other patterns for:</div>
            <ul className="space-y-1 text-blue-700 dark:text-blue-400">
              <li>• Optional fields → Use <strong>Fallback Pattern</strong></li>
              <li>• Show/hide content → Use <strong>Conditional</strong></li>
              <li>• Different messages → Use <strong>Conditional Else</strong></li>
              <li>• Complex logic → Use <strong>Advanced Pattern</strong></li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Quick Start Templates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-purple-200 dark:border-purple-800"
      >
        <div className="flex items-center mb-2">
          <Zap className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">
            Quick Start Templates
          </h3>
        </div>
        <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
          Pick a template to auto-fill a recommended data key, then customize it in configuration.
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          {templates.map((template) => (
            <motion.button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              onMouseEnter={() => setExpandedExample(template.id)}
              onMouseLeave={() => setExpandedExample(null)}
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500 text-left transition-all"
            >
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                {template.title}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                {template.description}
              </p>
              <div className="text-xs">
                <code className="bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 px-2 py-1 rounded">
                  {template.dataKey}
                </code>
              </div>

              {expandedExample === template.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700"
                >
                  <div className="text-xs space-y-1">
                    <div className="text-gray-600 dark:text-gray-400">
                      <strong>Syntax:</strong> {template.example}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      <strong>Result:</strong> {template.result}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Configuration Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white mb-2">
          Configure Your Data Key
        </h3>
        <p className="text-sm text-[#6E6D7A] dark:text-gray-400 mb-4">
          Enter the exact field name provided by your data source. This field will be inserted wherever you place the macro.
        </p>

        <div className="space-y-6">
          {/* Data Key Input */}
          <DataKeyInput
            value={localState.dataKey}
            onChange={(value) => setLocalState(prev => ({ ...prev, dataKey: value }))}
            platform={wizardState.platform}
            label="Data Key"
            placeholder="e.g., first_name, customer_name"
            helpText="Use the exact key name from your platform payload"
            required={true}
          />

          {/* Description Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Description (Optional)
            </label>
            <textarea
              value={localState.description}
              onChange={(e) => setLocalState(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Document when this macro should be used..."
              rows={3}
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add context so teammates can reuse this macro correctly
            </p>
          </div>
        </div>
      </motion.div>

      {/* Live Syntax Preview */}
      {generatedSyntax && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Code className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                Generated Syntax
              </h3>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-3 py-1 text-sm bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-700 transition-colors"
            >
              <Eye className="w-4 h-4 mr-1" />
              {showPreview ? 'Hide' : 'Show'} Test
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <code className="flex-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-4 py-3 rounded border font-mono text-sm">
              {generatedSyntax}
            </code>
            <button
              onClick={() => copyToClipboard(generatedSyntax)}
              className="p-2 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-700 transition-colors"
              title="Copy syntax"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          
          {showPreview && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-700"
            >
              <div className="flex items-center mb-3">
                <TestTube className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                <h4 className="font-semibold text-green-900 dark:text-green-100">
                  Test Your Macro
                </h4>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Test value for "{localState.dataKey}":
                  </label>
                  <input
                    type="text"
                    value={testValue}
                    onChange={(e) => setTestValue(e.target.value)}
                    placeholder={`Enter test value for ${localState.dataKey}...`}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                {testValue && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <strong>Result:</strong>
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {testValue}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Example Scenarios */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
      >
        <h4 className="font-semibold text-[#1A2B49] dark:text-white mb-3">
          Example Use Cases
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">✅ Great for:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Required customer names</li>
              <li>• Order confirmation numbers</li>
              <li>• Account usernames</li>
              <li>• Product names in orders</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">❌ Not ideal for:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Optional profile fields</li>
              <li>• Data that might be missing</li>
              <li>• Conditional messaging needs</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6E6D7A] hover:text-[#1A2B49] dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          ← Back to pattern selection
        </button>

        {isValid && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-sm text-green-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ready to preview
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default SimplePatternStep